import { useOrganization } from 'context/OrganizationContext';
import { BreedingPlan, BreedingplanService, Product, ProductTypeEnum, SupplierOrderDetail } from 'openapi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { OptionItemInterface } from 'ui/Inputs/SelectInput';
import type { SaveSemenOrder } from '../index';

interface ReturnType {
  breedingPlanOptions: OptionItemInterface[];
  selectedPlan: BreedingPlan | undefined;
}

interface Props {
  setValue: UseFormSetValue<SaveSemenOrder>;
  watch: UseFormWatch<SaveSemenOrder>;
  existingOrder: SupplierOrderDetail | undefined;
  selectedMareCustomer: string | undefined;
  modalIsOpen: boolean;
  products: Product[];
}

/**
 * Small hook to handle the breeding plan selection for the semen order
 */
function useSemenOrderBreedingPlan({ setValue, watch, existingOrder, selectedMareCustomer, modalIsOpen, products }: Props): ReturnType {
  const [breedingPlans, setBreedingPlans] = useState<BreedingPlan[]>();

  const { selectedOrganizationUid } = useOrganization();
  const breedingPlanUidFormValue = watch('plan_uid');
  const selectedPlan = breedingPlans?.find(plan => plan.uid === breedingPlanUidFormValue);

  const breedingPlanOptions = useMemo((): OptionItemInterface[] => {
    return (breedingPlans ?? []).map(
      plan =>
        ({
          id: plan.uid,
          name: String(plan.number),
        }) as OptionItemInterface,
    );
  }, [breedingPlans]);

  /* Load the Breeding Plans
   */
  const loadBreedinPlans = useCallback(
    async (mareUid: string) => {
      if (!selectedOrganizationUid) return;
      const result = await BreedingplanService.breedingplanList({
        mareOrganisationUid: selectedOrganizationUid,
        mareUid,
      });

      setBreedingPlans(result.results);
    },
    [selectedOrganizationUid],
  );

  /**
   * Load the breeding plans for the selected mare
   */
  useEffect(() => {
    if (selectedMareCustomer && modalIsOpen) {
      const [mareUid] = selectedMareCustomer?.split('-') ?? [undefined, undefined];
      loadBreedinPlans(mareUid);
    } else {
      setBreedingPlans(undefined);
    }
  }, [existingOrder, loadBreedinPlans, modalIsOpen, selectedMareCustomer]);

  /**
   * Set the values of the breeding plan if we have a selected breeding plan
   * but just for a new order
   */
  useEffect(() => {
    if (!existingOrder && breedingPlanUidFormValue) {
      if (selectedPlan) {
        setValue('semen_type', selectedPlan.semen_type);
        setValue('usage_type', selectedPlan.usage_type);
        setValue('requester_uid', selectedPlan?.requester_uid ?? undefined);
        setValue('studbook', selectedPlan.studbook);

        // for stallion we need to look for the correct product
        const stallionProduct = products.find(
          prod => prod.product_type === ProductTypeEnum.BREEDING && prod.stallion_uid === selectedPlan.stallion_uid,
        );
        setValue('product', stallionProduct?.uid);
      }
    }
  }, [breedingPlanUidFormValue, breedingPlans, existingOrder, products, selectedPlan, setValue]);

  /**
   * In some situations its impossible by the browser to select the correct breeding plan
   * as we first have the value and after that we receive the options.
   * To bypass this we set the value again if the options are loaded
   */
  useEffect(() => {
    setValue('plan_uid', breedingPlanUidFormValue);
  }, [breedingPlanOptions]); //eslint-disable-line

  /**
   * In case we just have one plan, we could set the value directly
   */
  useEffect(() => {
    if (breedingPlans && breedingPlans.length === 1) {
      setValue('plan_uid', breedingPlans[0].uid);
    }
  }, [breedingPlans, setValue]);

  return {
    breedingPlanOptions,
    selectedPlan,
  };
}

export default useSemenOrderBreedingPlan;
